// src/services/contactService.js - Complete Enhanced Version with Cascade Delete & Note System
import {
  db,
  CONTACT_MESSAGES_COLLECTION,
  NOTIFICATIONS_COLLECTION,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch,
} from './firebase';

// API endpoint for notifications
const NOTIFY_API_URL = '/api/notify';

// ==================== HELPER FUNCTIONS ====================

// Helper to call notification API
async function sendNotification(notificationData) {
  try {
    const response = await fetch(NOTIFY_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(notificationData),
    });

    const result = await response.json();

    if (!result.success) {
      console.warn('Notification API returned error:', result.message);
      return { success: false, error: result.message };
    }

    console.log('✅ Notification sent successfully:', result);
    return result;
  } catch (error) {
    console.error('Failed to send notification:', error);
    return { success: false, error: error.message };
  }
}

// Validate email format
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Generate unique ID for notes
function generateNoteId() {
  return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Simple spam detection
function isSpamContent(message, subject) {
  const spamKeywords = [
    'viagra',
    'casino',
    'porn',
    'xxx',
    'gambling',
    'lottery',
    'prize',
    'winner',
    'bitcoin',
    'crypto',
    'investment',
    'million',
    'dollar',
    'offer',
    'discount',
    'free money',
    'click here',
    'earn money',
    'work from home',
    'make money',
    'get rich',
    'double your',
    'guaranteed',
    'unsubscribe',
    'bulk email',
    'mass email',
    'email list',
    'opt out',
  ];

  const content = `${subject || ''} ${message || ''}`.toLowerCase();

  // Check for spam keywords
  for (const keyword of spamKeywords) {
    if (content.includes(keyword)) {
      return true;
    }
  }

  // Check for excessive links (spam indicator)
  const linkCount = (content.match(/https?:\/\/[^\s]+/g) || []).length;
  if (linkCount > 3) {
    return true;
  }

  // Check for excessive special characters
  const specialChars = (
    content.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g) || []
  ).length;
  if (specialChars > 20) {
    return true;
  }

  return false;
}

// ==================== CONTACT SERVICE ====================

export const contactService = {
  // =============================================
  // SUBMIT NEW CONTACT MESSAGE
  // =============================================

  async submitMessage(messageData) {
    try {
      // Validate email
      if (!isValidEmail(messageData.email)) {
        throw new Error('Invalid email address');
      }

      // Check for spam
      const isSpam = isSpamContent(messageData.message, messageData.subject);

      const now = new Date().toISOString();

      const message = {
        name: messageData.name?.trim(),
        email: messageData.email?.trim(),
        subject: messageData.subject?.trim() || 'General Inquiry',
        message: messageData.message?.trim(),
        status: isSpam ? 'spam' : 'unread',
        isSpam: isSpam,
        isArchived: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        notes: [],
        notificationIds: [],
        respondedAt: null,
        responseMessage: null,
        respondedBy: null,
        emailResponseSent: false,
        emailResponseSentAt: null,
        emailResponse: null,
        ipAddress: null,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        submittedAt: now,
        metadata: {
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : null,
          pageUrl: typeof window !== 'undefined' ? window.location.href : null,
          referralSource:
            typeof document !== 'undefined' ? document.referrer : 'direct',
          submittedAt: now,
        },
      };

      // Clean up undefined values
      Object.keys(message).forEach(key => {
        if (message[key] === undefined) {
          delete message[key];
        }
      });

      const docRef = await addDoc(
        collection(db, CONTACT_MESSAGES_COLLECTION),
        message,
      );

      // =============================================
      // SEND NOTIFICATION WITH INQUIRY ID - FIXED
      // =============================================
      if (!isSpam) {
        const notificationResult = await sendNotification({
          type: 'contact',
          inquiryData: {
            name: messageData.name,
            email: messageData.email,
            subject: messageData.subject || 'General Inquiry',
            message: messageData.message,
            // FIX: Pass the document ID as inquiryId
            inquiryId: docRef.id,
            messageId: docRef.id,
            phone: messageData.phone || null,
            status: 'unread',
          },
          adminUserId: null,
          sendEmail: true,
          useAdminTemplate: true,
        });

        // Save notification ID if created
        if (notificationResult?.data?.notificationId) {
          await updateDoc(docRef, {
            notificationIds: arrayUnion(notificationResult.data.notificationId),
          });
        }

        return {
          id: docRef.id,
          ...message,
          notificationSent: notificationResult?.success || false,
          notificationId: notificationResult?.data?.notificationId || null,
        };
      }

      return { id: docRef.id, ...message, isSpam: true };
    } catch (error) {
      console.error('Error submitting message:', error);
      throw error;
    }
  },

  // =============================================
  // GET ALL MESSAGES (Admin)
  // =============================================

  async getMessages(filters = {}, lastDoc = null, limitCount = 20) {
    try {
      let q = query(
        collection(db, CONTACT_MESSAGES_COLLECTION),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      );

      if (filters.status && filters.status !== 'all') {
        q = query(
          collection(db, CONTACT_MESSAGES_COLLECTION),
          where('status', '==', filters.status),
          orderBy('createdAt', 'desc'),
          limit(limitCount),
        );
      }

      if (filters.isArchived !== undefined) {
        q = query(
          collection(db, CONTACT_MESSAGES_COLLECTION),
          where('isArchived', '==', filters.isArchived),
          orderBy('createdAt', 'desc'),
          limit(limitCount),
        );
      }

      if (filters.isSpam !== undefined) {
        q = query(
          collection(db, CONTACT_MESSAGES_COLLECTION),
          where('isSpam', '==', filters.isSpam),
          orderBy('createdAt', 'desc'),
          limit(limitCount),
        );
      }

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const messages = [];
      let lastVisible = null;

      snapshot.forEach(doc => {
        messages.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });

      return {
        messages,
        lastVisible,
        hasMore: snapshot.docs.length === limitCount,
      };
    } catch (error) {
      console.error('Error getting messages:', error);
      throw error;
    }
  },

  // =============================================
  // GET SINGLE MESSAGE
  // =============================================

  async getMessage(id) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting message:', error);
      throw error;
    }
  },

  // =============================================
  // UPDATE MESSAGE STATUS
  // =============================================

  async updateMessageStatus(
    id,
    status,
    adminId = null,
    responseMessage = null,
  ) {
    try {
      const updateData = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (responseMessage) {
        updateData.responseMessage = responseMessage;
        updateData.respondedAt = serverTimestamp();
        updateData.respondedBy = adminId;
      }

      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      console.error('Error updating message status:', error);
      throw error;
    }
  },

  // =============================================
  // UPDATE MESSAGE WITH EMAIL RESPONSE
  // =============================================

  async updateMessageWithEmailResponse(id, status, adminId, responseMessage) {
    try {
      const updateData = {
        status,
        updatedAt: serverTimestamp(),
        responseMessage,
        respondedAt: serverTimestamp(),
        respondedBy: adminId,
        emailResponseSent: true,
        emailResponseSentAt: serverTimestamp(),
        emailResponse: responseMessage,
      };

      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      console.error('Error updating message with email response:', error);
      throw error;
    }
  },

  // =============================================
  // TOGGLE ARCHIVE
  // =============================================

  async toggleArchive(id, isArchived) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      await updateDoc(docRef, {
        isArchived,
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Error toggling archive:', error);
      throw error;
    }
  },

  // =============================================
  // MARK AS SPAM
  // =============================================

  async markAsSpam(id, isSpam) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      await updateDoc(docRef, {
        isSpam,
        status: isSpam ? 'spam' : 'unread',
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Error marking as spam:', error);
      throw error;
    }
  },

  // =============================================
  // NOTE SYSTEM
  // =============================================

  async addNote(id, note, adminId) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingNotes = docSnap.data()?.notes || [];

      const newNote = {
        id: generateNoteId(),
        note: note.trim(),
        addedBy: adminId,
        addedByName: 'Admin',
        addedAt: new Date().toISOString(),
        isEdited: false,
      };

      await updateDoc(docRef, {
        notes: [...existingNotes, newNote],
        updatedAt: serverTimestamp(),
      });

      return newNote;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  },

  async updateNote(id, noteId, updatedNote, adminId) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingNotes = docSnap.data()?.notes || [];

      const updatedNotes = existingNotes.map(note =>
        note.id === noteId
          ? {
              ...updatedNote,
              editedBy: adminId,
              editedAt: new Date().toISOString(),
            }
          : note,
      );

      await updateDoc(docRef, {
        notes: updatedNotes,
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  async deleteNote(id, noteId, adminId) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      const existingNotes = docSnap.data()?.notes || [];

      const updatedNotes = existingNotes.filter(note => note.id !== noteId);

      await updateDoc(docRef, {
        notes: updatedNotes,
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  // =============================================
  // CASCADE DELETE - Delete message and all related data
  // =============================================

  async cascadeDeleteMessage(id) {
    try {
      const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Message not found');
      }

      const messageData = docSnap.data();
      const deletedItems = {
        message: false,
        notes: 0,
        notifications: 0,
        notificationsDeleted: [],
        messageId: id,
      };

      console.log(`🗑️ Starting cascade delete for contact message: ${id}`);

      // 1. Delete all notifications associated with this message
      const notificationIds = messageData.notificationIds || [];

      if (notificationIds.length > 0) {
        console.log(
          `📧 Found ${notificationIds.length} notification(s) to delete`,
        );
        const notificationBatch = writeBatch(db);
        let notificationCount = 0;

        for (const notificationId of notificationIds) {
          try {
            const notifRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
            notificationBatch.delete(notifRef);
            notificationCount++;
            deletedItems.notificationsDeleted.push(notificationId);
            console.log(`✅ Queued notification deletion: ${notificationId}`);
          } catch (error) {
            console.error(
              `❌ Error queuing notification ${notificationId}:`,
              error,
            );
          }
        }

        if (notificationCount > 0) {
          await notificationBatch.commit();
          deletedItems.notifications = notificationCount;
          console.log(`✅ Deleted ${notificationCount} notifications`);
        }
      } else {
        console.log('ℹ️ No notifications found to delete');
      }

      // 2. Also try to find notifications by data.inquiryId (fallback for older entries)
      try {
        const notificationsQuery = query(
          collection(db, NOTIFICATIONS_COLLECTION),
          where('data.inquiryId', '==', id),
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);

        if (!notificationsSnapshot.empty) {
          const fallbackBatch = writeBatch(db);
          let fallbackCount = 0;

          notificationsSnapshot.forEach(doc => {
            fallbackBatch.delete(doc.ref);
            fallbackCount++;
            deletedItems.notificationsDeleted.push(doc.id);
            console.log(`✅ Found and queued fallback notification: ${doc.id}`);
          });

          await fallbackBatch.commit();
          deletedItems.notifications += fallbackCount;
          console.log(`✅ Deleted ${fallbackCount} fallback notifications`);
        }
      } catch (error) {
        console.error('❌ Error deleting fallback notifications:', error);
      }

      // 3. Also try to find notifications with email reference (fallback)
      try {
        if (messageData.email) {
          const emailNotificationsQuery = query(
            collection(db, NOTIFICATIONS_COLLECTION),
            where('data.email', '==', messageData.email),
          );
          const emailNotificationsSnapshot = await getDocs(
            emailNotificationsQuery,
          );

          if (!emailNotificationsSnapshot.empty) {
            const emailBatch = writeBatch(db);
            let emailCount = 0;

            emailNotificationsSnapshot.forEach(doc => {
              if (!deletedItems.notificationsDeleted.includes(doc.id)) {
                emailBatch.delete(doc.ref);
                emailCount++;
                deletedItems.notificationsDeleted.push(doc.id);
                console.log(
                  `✅ Found and queued email notification: ${doc.id}`,
                );
              }
            });

            if (emailCount > 0) {
              await emailBatch.commit();
              deletedItems.notifications += emailCount;
              console.log(`✅ Deleted ${emailCount} email notifications`);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error deleting email notifications:', error);
      }

      // 4. Notes are embedded in the message document
      deletedItems.notes = messageData.notes?.length || 0;

      // 5. Delete the message document itself
      await deleteDoc(docRef);
      deletedItems.message = true;
      console.log(`✅ Message document deleted: ${id}`);

      console.log(
        `✅ Cascade delete complete for message ${id}:`,
        deletedItems,
      );
      return deletedItems;
    } catch (error) {
      console.error('Error in cascade delete:', error);
      throw error;
    }
  },

  // =============================================
  // DELETE MESSAGE (with cascade)
  // =============================================

  async deleteMessage(id) {
    return await this.cascadeDeleteMessage(id);
  },

  // =============================================
  // BULK ACTION (with cascade delete)
  // =============================================

  async bulkAction(ids, action, value = null) {
    if (!ids || ids.length === 0) return { success: true, count: 0 };

    try {
      // For delete action, use cascade delete for each item
      if (action === 'delete') {
        let deletedCount = 0;
        let failedCount = 0;
        const results = [];

        for (const id of ids) {
          try {
            const result = await this.cascadeDeleteMessage(id);
            deletedCount++;
            results.push({ id, success: true, ...result });
            console.log(`✅ Deleted message ${id} with cascade`);
          } catch (err) {
            failedCount++;
            results.push({ id, success: false, error: err.message });
            console.error(`❌ Failed to delete message ${id}:`, err);
          }
        }

        return {
          success: true,
          deletedCount,
          failedCount,
          results,
        };
      }

      // For other actions, use batch
      const batch = writeBatch(db);

      ids.forEach(id => {
        const docRef = doc(db, CONTACT_MESSAGES_COLLECTION, id);

        switch (action) {
          case 'archive':
            batch.update(docRef, {
              isArchived: true,
              updatedAt: serverTimestamp(),
            });
            break;
          case 'unarchive':
            batch.update(docRef, {
              isArchived: false,
              updatedAt: serverTimestamp(),
            });
            break;
          case 'mark-read':
            batch.update(docRef, {
              status: 'read',
              updatedAt: serverTimestamp(),
            });
            break;
          case 'mark-unread':
            batch.update(docRef, {
              status: 'unread',
              updatedAt: serverTimestamp(),
            });
            break;
          case 'spam':
            batch.update(docRef, {
              isSpam: true,
              status: 'spam',
              updatedAt: serverTimestamp(),
            });
            break;
          case 'not-spam':
            batch.update(docRef, {
              isSpam: false,
              status: 'unread',
              updatedAt: serverTimestamp(),
            });
            break;
          default:
            console.warn(`Unknown bulk action: ${action}`);
        }
      });

      await batch.commit();
      console.log(
        `✅ Bulk action "${action}" completed for ${ids.length} items`,
      );
      return { success: true, count: ids.length };
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  },

  // =============================================
  // GET STATS
  // =============================================

  async getStats() {
    try {
      const snapshot = await getDocs(
        collection(db, CONTACT_MESSAGES_COLLECTION),
      );
      let unread = 0;
      let read = 0;
      let archived = 0;
      let spam = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.isSpam) spam++;
        else if (data.isArchived) archived++;
        else if (data.status === 'unread') unread++;
        else if (data.status === 'read' || data.status === 'responded') read++;
      });

      return {
        total: snapshot.size,
        unread,
        read,
        archived,
        spam,
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return { total: 0, unread: 0, read: 0, archived: 0, spam: 0 };
    }
  },

  // =============================================
  // GET TIMELINE DATA
  // =============================================

  async getTimelineData(days = 30) {
    try {
      const snapshot = await getDocs(
        collection(db, CONTACT_MESSAGES_COLLECTION),
      );
      const timeline = {};
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      for (let i = days; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateKey = date.toISOString().split('T')[0];
        timeline[dateKey] = 0;
      }

      snapshot.forEach(doc => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate();
        // Only include non-spam messages in timeline
        if (createdAt && createdAt >= startDate && !data.isSpam) {
          const dateKey = createdAt.toISOString().split('T')[0];
          if (timeline[dateKey] !== undefined) {
            timeline[dateKey]++;
          }
        }
      });

      return Object.entries(timeline).map(([date, count]) => ({ date, count }));
    } catch (error) {
      console.error('Error getting timeline data:', error);
      return [];
    }
  },

  // =============================================
  // GET MESSAGE BY EMAIL (Utility)
  // =============================================

  async getMessagesByEmail(email) {
    try {
      const q = query(
        collection(db, CONTACT_MESSAGES_COLLECTION),
        where('email', '==', email),
        orderBy('createdAt', 'desc'),
      );
      const snapshot = await getDocs(q);
      const messages = [];
      snapshot.forEach(doc => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      return messages;
    } catch (error) {
      console.error('Error getting messages by email:', error);
      return [];
    }
  },

  // =============================================
  // GET RECENT MESSAGES (Utility)
  // =============================================

  async getRecentMessages(limitCount = 5) {
    try {
      const q = query(
        collection(db, CONTACT_MESSAGES_COLLECTION),
        where('isSpam', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      const messages = [];
      snapshot.forEach(doc => {
        messages.push({ id: doc.id, ...doc.data() });
      });
      return messages;
    } catch (error) {
      console.error('Error getting recent messages:', error);
      return [];
    }
  },

  // =============================================
  // GET UNREAD COUNT (Utility)
  // =============================================

  async getUnreadCount() {
    try {
      const q = query(
        collection(db, CONTACT_MESSAGES_COLLECTION),
        where('status', '==', 'unread'),
        where('isSpam', '==', false),
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  },

  // =============================================
  // SEARCH MESSAGES (Utility)
  // =============================================

  async searchMessages(searchTerm) {
    try {
      // This is a simple implementation - for production, consider using Algolia or Elasticsearch
      const snapshot = await getDocs(
        collection(db, CONTACT_MESSAGES_COLLECTION),
      );
      const results = [];
      const term = searchTerm.toLowerCase();

      snapshot.forEach(doc => {
        const data = doc.data();
        const match =
          data.name?.toLowerCase().includes(term) ||
          data.email?.toLowerCase().includes(term) ||
          data.subject?.toLowerCase().includes(term) ||
          data.message?.toLowerCase().includes(term);

        if (match) {
          results.push({ id: doc.id, ...data });
        }
      });

      return results;
    } catch (error) {
      console.error('Error searching messages:', error);
      return [];
    }
  },

  // =============================================
  // BULK DELETE BY FILTER (Utility)
  // =============================================

  async bulkDeleteByFilter(filterType, value) {
    try {
      let q;
      switch (filterType) {
        case 'spam':
          q = query(
            collection(db, CONTACT_MESSAGES_COLLECTION),
            where('isSpam', '==', true),
          );
          break;
        case 'archived':
          q = query(
            collection(db, CONTACT_MESSAGES_COLLECTION),
            where('isArchived', '==', true),
          );
          break;
        case 'status':
          q = query(
            collection(db, CONTACT_MESSAGES_COLLECTION),
            where('status', '==', value),
          );
          break;
        default:
          throw new Error('Invalid filter type');
      }

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      let count = 0;

      snapshot.forEach(doc => {
        batch.delete(doc.ref);
        count++;
      });

      if (count > 0) {
        await batch.commit();
      }

      return { deleted: count };
    } catch (error) {
      console.error('Error in bulk delete by filter:', error);
      throw error;
    }
  },
};

export default contactService;
